/**
 * Performance, Efficiency tab: its types and routes. Served by
 * src/aithora/v3/finance/efficiency.ts. Every line carries its basis; a figure with no
 * source is null with basis LOCKED (never 0): hours returned, the national labor-share
 * and CNA-turnover indices, the fill-a-shift hours and rates, the loaded RN rate and
 * the quarter's hour totals all wait on a time-saved measure or a payroll and agency
 * feed. The page takes occupancy (beds, residents) and the operating margin from the
 * building's benchmark.
 */
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';

/**
 * Where a line's figure comes from: OURS = our own records, CMS = CMS published for this
 * building, MODEL = a cited range on a published baseline, LOCKED = no local value yet.
 */
export type EfficiencyBasis = 'OURS' | 'CMS' | 'MODEL' | 'LOCKED';
/** A strip figure's tag: INDEX = a published national index; PART MODEL = some of it modeled. */
export type EfficiencyTag = EfficiencyBasis | 'INDEX' | 'PART MODEL';

/** One line of the operating register. Money is dollars per month. */
export interface EfficiencyLine {
  key: string;
  line: string;
  savedUsd: number;
  atRiskUsd: number;
  basis: EfficiencyBasis;
  /** The baseline or published index, in `unit`; null when there is none. */
  before: number | null;
  /** This building now, in `unit`; null when there is no local value. */
  now: number | null;
  /** '$', 'hrs', '%', 'entries', '$ PPD' and the like; '' for none. */
  unit: string;
  /** Where the figure comes from, one paragraph. */
  source: string;
  action: string;
  owner: string;
}

/** GET /api/aithora/performance/efficiency?facility=: the operating register for one building. */
export interface EfficiencyBoard {
  facilityId: string | null;
  period: string;
  real: boolean;
  lines: EfficiencyLine[];
  /** Cost centres (panels) and which lines each holds, and how they stack into columns. */
  centres: { name: string; keys: string[] }[];
  stacks: string[][];
  /** Hours given back per month, and their basis. Null (LOCKED) until a time-saved measure exists. */
  hoursReturned: { value: number | null; basis: EfficiencyTag; reason?: string };
  /** Published national indices shown on the strip. Null (LOCKED) with a reason when no source is on file. */
  indices: {
    labor: { value: number | null; basis: EfficiencyTag; reason?: string };
    cnaTurnover: { value: number | null; basis: EfficiencyTag; reason?: string };
  };
  /** The basis of the operating margin figure (the value itself comes from the benchmark). */
  marginBasis: EfficiencyTag;
  /** Before Aithora and now, on the lines that have a baseline. */
  delta: { label: string; before: number; now: number; unit: string; basis: EfficiencyTag; lowerIsBetter: boolean }[];
  deltaNote: string;
  /** The quarter to date: captured dollars per week from our claims; hour figures are null until measured. */
  quarter: { label: string; basis: EfficiencyTag; weeksHours: number[]; weeksCapturedUsd: number[]; totalHours: number | null; valueUsd: number; hoursPerMonth: number | null; windowLabel: string };
  /** Where the hours went, as shares of the hours returned. */
  flow: { basis: EfficiencyTag; rows: { k: string; share: number }[]; note: string };
  /** What one open shift costs to fill, per hour, by route. Hours null and rows empty until a payroll and agency feed exists. */
  fill: { hours: number | null; role: string; rows: { k: string; ratePerHour: number }[]; note: string };
  /** Cost per resident day: what each payer pays, and the national cost-centre split. */
  ppd: { basis: EfficiencyTag; payers: { name: string; ratePerDay: number; share: number }[]; shares: { centre: string; share: number }[] };
  /** Levers, each in dollars per month against this building's revenue. */
  scenarios: { k: string; usd: number }[];
  /** Loaded RN cost per hour; null until a payroll feed exists. */
  loadedRnRate: number | null;
}

const A = AITHORA_PREFIX;
/** GET /api/aithora/performance/efficiency?facility=: served (src/aithora/v3/finance/efficiency.ts). */
export const performanceEfficiency = route<EfficiencyBoard, { facility?: string }>()('GET', `${A}/performance/efficiency`, { honest: true });
